import get from 'lodash.get';
import React, { useState } from 'react';
import classNames from 'classnames';

import {
  BylineBio,
  BylineName,
  BylineSocial,
  BylineThumbnail,
} from 'components/Byline';
import { getDataActivityMapID } from 'lib/articleUtils';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { Accordion } from 'components/Accordion';
import { LinkIfHref } from 'components/Link';

/* Important: Making changes to this file could have an impact on shorthand articles */

const block = 'expanded-byline';

const dataActivityMapID = getDataActivityMapID({
  pageRegion: 'article-bottom',
  componentName: 'expanded-byline',
});

/**
 * ExpandedByline component displays author information including thumbnail, name, social links, and bio.
 * It also optionally renders a list of the author's latest articles.
 *
 * @param {object} props - The component props.
 * @param {object} props.author - The author object containing person details.
 * @param {string} props.vertical - The vertical type to determine content rendering.
 * @param {string} props.containerClass - Additional CSS classes for the container.
 * @param {string} [props.position] - The position of the byline.
 */
export const ExpandedByline = ({
  author = {}, vertical = '', containerClass = '', position,
}) => {
  const { 'author-byline-recirc-and-social-media-updates': showAuthorBylineRecircAndSocialUpdates } = useFeatureFlagContext();
  const [isOpen, setIsOpen] = useState(false);
  // Account for different anatomies of the person object
  const person = get(author, 'person', author);
  const thumbnail = get(person, ['primaryImage', 'url', 'primary'], '');
  const noThumbnailClass = !thumbnail ? 'no-thumbnail' : '';

  if (!person) {
    return null;
  }
  const MAX_AUTHOR_CONTENT_TO_DISPLAY = 3;
  const shouldRenderAuthorContentRecirc = vertical === 'select' && showAuthorBylineRecircAndSocialUpdates;
  const authorContent = person?.content?.items?.length > MAX_AUTHOR_CONTENT_TO_DISPLAY
    ? person.content.items.slice(0, MAX_AUTHOR_CONTENT_TO_DISPLAY)
    : person?.content?.items;

  return (
    <div
      className={classNames(block, containerClass, noThumbnailClass)}
      data-activity-map={dataActivityMapID}
    >
      <BylineThumbnail
        person={person}
        additionalClasses={`${block}__thumbnail`}
        additionalClassesNoImage={`${block}__thumbnail`}
        position={position}
      />
      <div className={`${block}__info`}>
        <BylineName
          person={person}
          additionalClasses={`${block}__name`}
          position={position}
        />
        <BylineSocial
          person={person}
          additionalClasses={`${block}__social`}
        />
      </div>
      <BylineBio
        person={person}
        additionalClasses={`${block}__bio ${noThumbnailClass}`}
      />
      {shouldRenderAuthorContentRecirc && authorContent?.length > 0 && (
        <Accordion
          title={isOpen ? 'Collapse' : 'Latest articles'}
          openDirection="up"
          isOpen={isOpen}
          onToggle={() => setIsOpen((prev) => !prev)}
          additionalStyles={{
            accordion: `${block}__accordion`,
            accordion__header: `${block}__show-more-button`,
            accordion__icon: `${block}__accordion-icon`,
            accordion__content: `${block}__article-list`,
            open: `${block}__accordion--open`,
            closed: `${block}__accordion--closed`,
          }}
        >
          <ul data-testid="byline-article-recirc">
            {authorContent.map((contentItem) => (
              <li>
                <LinkIfHref key={contentItem?.headline?.primary} href={contentItem?.url?.primary}>
                  {contentItem?.headline?.primary}
                </LinkIfHref>
              </li>
            ))}
          </ul>
        </Accordion>
      )}
    </div>
  );
};
